import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, LogOut, Save, X, Mic, MicOff, Globe, Loader2, PlusCircle, FileText, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import LanguageSelector from "@/components/LanguageSelector";
import ScriptInitial from "@/components/ScriptInitial";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAG_OPTIONS = [
  "nightmare",
  "lucid",
  "lucidDream",
  "recurring",
  "vivid",
  "flying",
  "falling",
  "water",
  "chase",
  "adventure",
  "peaceful",
  "confusing",
  "thrilling",
  "romantic",
  "scary",
  "sad",
  "funny",
  "realistic",
  "sleepParalysis",
  "premonition",
  "forgotDream"
];

const TRANSCRIPTION_LANGUAGES = [
  { code: 'fi', name: 'Suomi', flag: '\u{1F1EB}\u{1F1EE}' },
  { code: 'en', name: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'fr', name: 'Fran\u00e7ais', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'de', name: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'es', name: 'Espa\u00f1ol', flag: '\u{1F1EA}\u{1F1F8}' }
];

export default function DreamJournal({ user, onLogout }) {
  const navigate = useNavigate();
  const { editId } = useParams();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionLanguage, setTranscriptionLanguage] = useState(i18n.language || 'en');
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingDream, setLoadingDream] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    date: new Date().toISOString().split('T')[0],
    tags: [],
    is_public: false
  });

  // Load existing dream for editing
  useEffect(() => {
    if (editId) {
      setIsEditMode(true);
      setLoadingDream(true);
      const token = localStorage.getItem('token');
      axios.get(`${API}/dreams/${editId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        const d = res.data;
        setFormData({
          title: d.title || "",
          content: d.content || "",
          date: d.date ? new Date(d.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          tags: d.tags || [],
          is_public: d.is_public || false
        });
      }).catch(() => {
        toast.error(t('common.error'));
        navigate('/dreams');
      }).finally(() => setLoadingDream(false));
    }
  }, [editId]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.info(t('journal.recordingStarted') || 'Recording... Speak your dream');
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      if (error.name === 'NotAllowedError') {
        toast.error(t('journal.microphoneNotAllowed') || 'Microphone access denied');
      } else {
        toast.error(t('common.error') || 'Could not access microphone');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success(t('journal.recordingStopped') || 'Recording stopped');
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const token = localStorage.getItem('token');
      const formDataObj = new FormData();
      formDataObj.append('audio', audioBlob, 'recording.webm');
      formDataObj.append('language', transcriptionLanguage);
      
      const response = await axios.post(`${API}/speech-to-text`, formDataObj, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success && response.data.text) {
        setFormData(prev => ({
          ...prev,
          content: prev.content + (prev.content ? ' ' : '') + response.data.text
        }));
        toast.success(t('common.success') || 'Transcription complete!');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error(error.response?.data?.detail || t('common.error') || 'Transcription failed');
    } finally {
      setIsTranscribing(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        is_draft: false
      };

      if (isEditMode && editId) {
        // Update existing dream and publish
        await axios.patch(`${API}/dreams/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('toast.dreamSaved'));
        navigate(`/dreams/${editId}`);
      } else {
        // Create new dream
        const response = await axios.post(`${API}/dreams`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('toast.dreamSaved'));
        navigate(`/dreams/${response.data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        title: formData.title || t('journal.untitledDraft'),
        content: formData.content || "",
        date: new Date(formData.date).toISOString(),
        tags: formData.tags,
        is_public: false,
        is_draft: true
      };

      if (isEditMode && editId) {
        await axios.patch(`${API}/dreams/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('journal.draftSaved'));
      } else {
        const response = await axios.post(`${API}/dreams`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('journal.draftSaved'));
        // Switch to edit mode after first save
        navigate(`/journal/edit/${response.data.id}`, { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSavingDraft(false);
    }
  };

  if (loadingDream) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-void/60" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
                <img src="/logo-new.png" alt="Uneksia" className="w-12 h-12" />
                <span className="text-2xl font-heading font-light text-void tracking-tight">{t('appName')}</span>
              </div>
              <div className="hidden md:flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dashboard"
                >
                  {t('nav.dashboard')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dreams')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dreams"
                >
                  {t('nav.myDreams')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/community-hub')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-community-hub"
                >
                  {t('nav.community')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/blog")}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-blog"
                >
                  {t("blog.title")}
                </Button>
                {user?.is_admin && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/admin')}
                    className="font-body text-red-500 hover:text-red-600 font-medium"
                    data-testid="nav-admin"
                  >
                    Admin
                  </Button>
                )}
                {!user?.is_premium && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/premium')}
                    className="font-body text-lucid hover:text-lucid/80 font-medium"
                    data-testid="nav-premium"
                  >
                    {t('nav.premium')}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector variant="minimal" />
              <Button
                variant="ghost"
                onClick={() => navigate('/settings')}
                className="text-void hover:text-void/80"
                data-testid="settings-button"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={onLogout}
                className="text-void hover:text-void/80"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-heading font-light text-void tracking-tight mb-2" data-testid="journal-title">
            <ScriptInitial>{isEditMode ? t('journal.editTitle') : t('journal.title')}</ScriptInitial>
          </h1>
          <p className="text-lg text-void/70 font-body tracking-wide italic">
            {t('journal.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="glass-effect rounded-3xl p-8" data-testid="dream-form">
          {/* Draft indicator */}
          {isEditMode && (
            <div className="mb-6 flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl" data-testid="draft-indicator">
              <FileText className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-700 font-body">{t('journal.editingDraft')}</span>
            </div>
          )}

          {/* Title */}
          <div className="mb-6">
            <Label htmlFor="title" className="text-void font-body text-lg mb-2">{t('journal.dreamTitle')}</Label>
            <Input
              id="title"
              type="text"
              placeholder={t('journal.dreamTitlePlaceholder')}
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="bg-white/50 border-transparent focus:border-ethereal focus:ring-2 focus:ring-ethereal/50 rounded-xl h-12"
              data-testid="title-input"
            />
          </div>

          {/* Date */}
          <div className="mb-6">
            <Label htmlFor="date" className="text-void font-body text-lg mb-2">{t('journal.date')}</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              className="bg-white/50 border-transparent focus:border-ethereal focus:ring-2 focus:ring-ethereal/50 rounded-xl h-12"
              required
              data-testid="date-input"
            />
          </div>

          {/* Content */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="content" className="text-void font-body text-lg">{t('journal.dreamContent')}</Label>
              <div className="flex items-center gap-2">
                {/* Language Selector for Transcription */}
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-void/60" />
                  <Select
                    value={transcriptionLanguage}
                    onValueChange={setTranscriptionLanguage}
                  >
                    <SelectTrigger className="w-[140px] h-9 bg-white/50 border-transparent text-sm" data-testid="transcription-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRANSCRIPTION_LANGUAGES.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <span className="flex items-center gap-2">
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Microphone Button */}
                <Button
                  type="button"
                  onClick={toggleRecording}
                  disabled={isTranscribing}
                  className={`rounded-full p-3 transition-all duration-300 ${
                    isRecording 
                      ? 'bg-red-500 text-white animate-pulse hover:bg-red-600' 
                      : isTranscribing
                        ? 'bg-void/20 text-void/50'
                        : 'bg-void/10 text-void hover:bg-void/20'
                  }`}
                  title={isRecording ? (t('journal.stopRecording') || 'Stop recording') : (t('journal.startRecording') || 'Start voice recording')}
                  data-testid="voice-record-button"
                >
                  {isTranscribing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-5 h-5" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
            
            {/* Recording/Transcribing Status */}
            {isRecording && (
              <div className="mb-2 flex items-center gap-2 text-red-500 font-body text-sm">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                {t('journal.listening') || 'Listening... Speak your dream'}
              </div>
            )}
            {isTranscribing && (
              <div className="mb-2 flex items-center gap-2 text-void/70 font-body text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('journal.transcribing') || 'Transcribing...'}
              </div>
            )}
            
            <Textarea
              id="content"
              placeholder={t('journal.dreamContentPlaceholder')}
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              className="bg-white/50 border-transparent focus:border-ethereal focus:ring-2 focus:ring-ethereal/50 rounded-xl min-h-[300px] resize-none"
              data-testid="content-textarea"
            />
          </div>

          {/* Tags */}
          <div className="mb-8">
            <Label className="text-void font-body text-lg mb-3 block">{t('journal.tags')}</Label>
            <div className="flex flex-wrap gap-2">
              {TAG_OPTIONS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-4 py-2 rounded-full font-body text-sm transition-all duration-300 ${
                    formData.tags.includes(tag)
                      ? tag === 'nightmare' 
                        ? 'bg-red-500/80 text-white'
                        : tag === 'lucidDream'
                          ? 'bg-indigo-500/80 text-white'
                          : tag === 'sleepParalysis'
                            ? 'bg-slate-700/80 text-white'
                            : tag === 'premonition'
                              ? 'bg-amber-500/80 text-white'
                              : tag === 'forgotDream'
                                ? 'bg-gray-500/80 text-white'
                                : 'bg-lucid text-void'
                      : tag === 'nightmare'
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : tag === 'lucidDream'
                          ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                          : tag === 'sleepParalysis'
                            ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                            : tag === 'premonition'
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                              : tag === 'forgotDream'
                                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                : 'bg-white/30 text-void/70 hover:bg-white/50'
                  }`}
                  data-testid={`tag-${tag}`}
                >
                  {t(`tags.${tag}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Public/Private Toggle */}
          <div className="mb-8">
            <Label className="text-void font-body text-lg mb-3 block">{t('journal.makePublic')}</Label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setFormData({...formData, is_public: false})}
                className={`px-6 py-3 rounded-full font-body transition-all duration-300 ${
                  !formData.is_public
                    ? 'bg-void text-white'
                    : 'bg-white/30 text-void/70 hover:bg-white/50'
                }`}
                data-testid="private-button"
              >
                Private
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, is_public: true})}
                className={`px-6 py-3 rounded-full font-body transition-all duration-300 ${
                  formData.is_public
                    ? 'bg-lucid text-void'
                    : 'bg-white/30 text-void/70 hover:bg-white/50'
                }`}
                data-testid="public-button"
              >
                Public
              </button>
            </div>
            <p className="text-sm text-void/60 font-body mt-2">
              {t('journal.publicDescription')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="submit"
              disabled={loading || !formData.title || !formData.content}
              className="flex-1 bg-void text-white hover:bg-void/90 rounded-full h-12 font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95"
              data-testid="save-dream-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? t('journal.saving') : isEditMode ? t('journal.publishDream') : t('journal.saveDream')}
            </Button>
            <Button
              type="button"
              onClick={handleSaveDraft}
              disabled={savingDraft}
              className="sm:flex-none bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 rounded-full h-12 px-6 font-body font-medium transition-all duration-300"
              data-testid="save-draft-button"
            >
              <FileText className="w-4 h-4 mr-2" />
              {savingDraft ? t('common.saving') : t('journal.saveDraft')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(isEditMode ? '/dreams' : '/dashboard')}
              className="sm:flex-none bg-white/50 text-void border-void/10 hover:bg-white/80 rounded-full h-12 px-6 font-body font-medium"
              data-testid="cancel-button"
            >
              <X className="w-4 h-4 mr-2" />
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
